<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">

      <!-- Brand logo-->
      <b-link class="brand-logo">
        <b-img
          fluid
          src="@/assets/images/logo/Udara360-logo.png"
          width="180"
        />
      </b-link>
      <!-- /Brand logo-->

      <!-- Left Text-->
      <b-col
        lg="8"
        class="d-none d-lg-flex p-0"
      >

        <div
          v-if="institution.loginPageBackgroundURL"
          class="auth-bg-image"
          role="img"
          aria-label="Institution background"
          :style="{
            backgroundImage: 'url(' + institution.loginPageBackgroundURL + ')',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            width: '100%',
            height: '100%',
          }"
        />
      </b-col>
      <!-- /Left Text-->

      <!-- Login-->
      <b-col
        lg="4"
        class="d-flex align-items-center auth-bg px-2 p-lg-5"
      >
        <b-col
          sm="8"
          md="6"
          lg="12"
          class="px-xl-2 mx-auto"
        >
          <b-card-title class="mb-1">
            Token Authentication
          </b-card-title>

          <b-card-text class="mb-2">
            We’ve sent a token to your email
            {{ email }}
          </b-card-text>

          <Alert
            :show="showFormError"
            :message="errorMessage"
          />

          <!-- form -->
          <validation-observer ref="verifyTokenForm">
            <b-form
              class="auth-login-form mt-2"
              @submit.prevent="login"
            >
              <!-- Verification code -->
              <InputField
                id="token"
                v-model="userDetails.userToken"
                label="Token"
                placeholder="Enter token"
                type="text"
                error-name="Token"
                rules="required"
              />

              <LinkText
                v-if="!hasResentToken && !isResendingToken"
                class="primary mb-2"
                label="Resend Token"
                :on-click="resendForgotPasswordToken"
              />

              <b-spinner
                v-if="!hasResentToken && isResendingToken"
                label="Loading..."
                class="mb-2"
                variant="primary"
              />

              <LinkText
                v-if="hasResentToken && !isResendingToken"
                class="secondary mb-2"
                label="Token has been resent"
              />

              <!-- submit button -->
              <Button
                label="Confirm"
                :loading="isLoading"
                :disabled="isDisabled"
                size="lg"
                block
                @click="verifyUserToken"
              />
            </b-form>
          </validation-observer>

          <b-card-text class="text-center mt-2">
            <b-link :to="{name:'auth-login'}">
              <feather-icon icon="ChevronLeftIcon" /> Back to login
            </b-link>
          </b-card-text>
        </b-col>
      </b-col>
    <!-- /Login-->
    </b-row>
  </div>
</template>

<script>
/* eslint-disable global-require */
import { ValidationObserver } from 'vee-validate'
import {
  BRow, BCol, BLink, BCardText, BCardTitle, BImg, BForm,
} from 'bootstrap-vue'
import { required } from '@validations'
import { mapActions, mapGetters } from 'vuex'
import { toastAlert } from '@core/mixins/toast'
import InputField from '../../forms/InputField.vue'
import Button from '../../components/button/Button.vue'
import Alert from '../../components/alert/Alert.vue'
import LinkText from '../../components/LinkText.vue'

export default {
  components: {
    BRow,
    BCol,
    BLink,
    BCardText,
    BCardTitle,
    BImg,
    BForm,
    ValidationObserver,
    InputField,
    Button,
    Alert,
    LinkText,
  },
  mixins: [toastAlert],
  data() {
    return {
      userDetails: {
        userToken: '',
      },
      email: '',
      hasResentToken: false,
      isResendingToken: false,
      isLoading: false,
      isDisabled: false,
      showFormError: false,
      errorMessage: '',
      status: '',
      // validation rules
      required,
    }
  },
  computed: {
    ...mapGetters({
      institution: 'auth/getInstitution',
    }),
  },
  watch: {
    hasResentToken(newValue) {
      if (newValue) {
        // eslint-disable-next-line no-return-assign
        setTimeout(() => (this.hasResentToken = false), 1000 * 3)
      }
    },
  },
  mounted() {
    this.getUser()
  },
  methods: {
    ...mapActions({
      verifyForgotPasswordToken: 'auth/verifyForgotPasswordToken',
      resendForgotPasswordNewToken: 'auth/resendForgotPasswordNewToken',
    }),
    async resendForgotPasswordToken() {
      try {
        const { user } = this.$route.params
        this.isResendingToken = true
        const response = await this.resendForgotPasswordNewToken({ username: user })
        const { status } = response
        if (status === 200) {
          this.isResendingToken = false
          this.hasResentToken = true
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          this.errorMessage = responseError.errorMessage
        })
      } finally {
        this.isResendingToken = false
      }
    },
    getUser() {
      const { user } = this.$route.params
      if (user) {
        this.email = user
      } else {
        this.$router.push({ name: 'auth-login' })
      }
    },
    verifyUserToken() {
      this.$refs.verifyTokenForm.validate().then(async success => {
        if (success) {
          try {
            this.showFormError = false
            this.isDisabled = true
            this.isLoading = true

            const { user } = this.$route.params

            const response = await this.verifyForgotPasswordToken({
              username: user,
              userToken: this.userDetails.userToken.trim(),
            })
            this.$router
              .push({ name: 'security-question', params: { question: response.data, username: user } })
            this.isDisabled = false
            this.isLoading = false

            return true
          } catch (error) {
            error.response.data.errors.forEach(responseError => {
              this.errorMessage = responseError.errorMessage
            })
            this.showFormError = true
          } finally {
            this.isDisabled = false
            this.isLoading = false
          }
        }
        return false
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';
</style>
